import { useState, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { gymApi } from '@/lib/api';
import { formatLocalDate } from '@/lib/utils';
import { Client, Status, PaymentTransaction } from '@/types';
import { ClientAvatar } from '@/components/ClientAvatar';
import {
  Eye,
  Copy,
  CreditCard,
  ShoppingCart,
  Calendar,
  Mail,
  Phone,
  Hash,
  Loader2,
  DollarSign,
  Receipt,
  Camera,
  Trash2,
  Smartphone,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ClientDetailSheetProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymId: string;
  onPhotoChange?: () => void;
}

export function ClientDetailSheet({ client, open, onOpenChange, gymId, onPhotoChange }: ClientDetailSheetProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [photoPath, setPhotoPath] = useState<string | null | undefined>(undefined);
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const [activeDevice, setActiveDevice] = useState<{ device_label?: string; bound_at?: string; last_seen_at?: string } | null>(null);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);
  const [isResettingDevice, setIsResettingDevice] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) {
      setShowHistory(false);
      setHistoryLoaded(false);
      setTransactions([]);
      setTotalSpent(0);
      setActiveDevice(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && client && gymId) {
      loadActiveDevice();
    }
  }, [open, client?.client_id, gymId]);

  const loadActiveDevice = async () => {
    if (!client) return;
    setIsDeviceLoading(true);
    try {
      const res = await gymApi.getClientDevice(gymId, client.client_id);
      setActiveDevice(res.device || null);
    } catch (error) {
      console.warn('No se pudo cargar el dispositivo del cliente:', error);
      setActiveDevice(null);
    } finally {
      setIsDeviceLoading(false);
    }
  };

  const handleResetDevice = async () => {
    if (!client) return;
    if (!window.confirm('¿Desvincular el dispositivo actual? El cliente podrá registrar uno nuevo en su próximo check-in.')) return;

    setIsResettingDevice(true);
    try {
      const res = await gymApi.resetClientDevice(gymId, client.client_id);
      if (res?.success) {
        toast({ title: 'Dispositivo desvinculado', description: res.message });
        await loadActiveDevice();
      } else {
        toast({ title: 'Error', description: res?.message || 'No se pudo desvincular el dispositivo', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo desvincular el dispositivo',
        variant: 'destructive',
      });
    } finally {
      setIsResettingDevice(false);
    }
  };

  useEffect(() => {
    setPhotoPath(client?.photo_path ?? null);
  }, [client?.client_id, client?.photo_path]);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !client) return;

    setIsPhotoBusy(true);
    try {
      const res = await gymApi.uploadClientPhoto(client.client_id, file, gymId);
      if (res.success) {
        setPhotoPath(res.photo_path ?? null);
        onPhotoChange?.();
        toast({ title: 'Foto actualizada', description: 'La foto de perfil se guardó correctamente' });
      } else {
        toast({ title: 'Error', description: res.error || 'No se pudo subir la foto', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({
        title: 'Error al subir foto',
        description: error?.message || 'No se pudo subir la foto',
        variant: 'destructive',
      });
    } finally {
      setIsPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!client) return;

    setIsPhotoBusy(true);
    try {
      const res = await gymApi.deleteClientPhoto(client.client_id, gymId);
      if (res.success) {
        setPhotoPath(null);
        onPhotoChange?.();
        toast({ title: 'Foto eliminada' });
      }
    } catch (error: any) {
      toast({
        title: 'Error al eliminar foto',
        description: error?.message || 'No se pudo eliminar la foto',
        variant: 'destructive',
      });
    } finally {
      setIsPhotoBusy(false);
    }
  };

  if (!client) return null;

  const statusVariants: Record<Status, 'success' | 'warning' | 'destructive' | 'muted'> = {
    active: 'success',
    pending: 'warning',
    inactive: 'muted',
    expired: 'destructive',
  };

  const statusLabels: Record<Status, string> = {
    active: 'Activo',
    pending: 'Pendiente',
    inactive: 'Inactivo',
    expired: 'Expirado',
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(client.client_id);
    toast({ title: 'ID copiado', description: client.client_id });
  };

  const handleLoadHistory = async () => {
    setShowHistory(true);
    if (historyLoaded) return;
    setIsHistoryLoading(true);
    try {
      const res = await gymApi.getClientPaymentHistory(client.client_id, gymId);
      if (res.success) {
        setTransactions(res.transactions);
        setTotalSpent(res.totalSpent);
        setHistoryLoaded(true);
      }
    } catch (error: any) {
      toast({
        title: 'Error al cargar historial',
        description: error?.message || 'No se pudo cargar el historial de pagos',
        variant: 'destructive',
      });
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-card border-l border-border overflow-y-auto">
        <SheetHeader className="pb-6 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Detalle del Cliente
          </SheetTitle>
          <SheetDescription className="text-base font-medium text-foreground">
            {client.name}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Info Card */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <ClientAvatar
                    name={client.name}
                    photoPath={photoPath}
                    className="h-12 w-12"
                    textClassName="text-sm"
                  />
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{client.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant={statusVariants[client.status]} className="text-[10px]">
                        {statusLabels[client.status]}
                      </Badge>
                      {client.age && (
                        <Badge variant="outline" className="text-[10px]">
                          {client.age} años
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo controls */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  disabled={isPhotoBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isPhotoBusy ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {photoPath ? 'Cambiar foto' : 'Subir foto'}
                </Button>
                {photoPath && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive shrink-0"
                    disabled={isPhotoBusy}
                    onClick={handleRemovePhoto}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground text-xs uppercase">ID:</span>
                  <span className="font-mono text-xs">{client.client_id}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={handleCopyId}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                {client.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-xs break-all">{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-xs">{client.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground text-xs uppercase">Registro:</span>
                  <span className="text-xs">{formatDate(client.created_at)}</span>
                </div>
                {client.current_end_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground text-xs uppercase">Membresía vigente:</span>
                    <span className="text-xs">{formatLocalDate(client.current_end_date)}</span>
                    {client.current_sessions != null && (
                      <Badge variant="info" className="text-[10px] ml-auto">
                        {client.current_sessions} sesiones
                      </Badge>
                    )}
                  </div>
                )}
                {client.emergency_contact && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-destructive/60 shrink-0" />
                    <span className="text-muted-foreground text-xs uppercase">Emergencia:</span>
                    <span className="text-xs">{client.emergency_contact}</span>
                  </div>
                )}
                {client.notes && (
                  <div className="mt-1 p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-xs text-muted-foreground">{client.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code at bottom of info card */}
            <div className="px-5 pb-5 flex justify-center">
              <div className="bg-white p-3 rounded-xl">
                <img
                  src={`https://quickchart.io/qr?text=${encodeURIComponent(client.qr_code)}&size=180`}
                  alt={`QR ${client.name}`}
                  className="w-32 h-32"
                />
              </div>
            </div>

            {/* Device binding */}
            <div className="px-5 pb-5 border-t pt-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5">
                    <Smartphone className="h-3.5 w-3.5 text-primary" /> Dispositivo de check-in
                  </p>
                  {isDeviceLoading ? (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" /> Cargando…
                    </p>
                  ) : activeDevice ? (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-xs font-medium line-clamp-2 break-all">
                        {activeDevice.device_label || 'Dispositivo registrado'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Vinculado: {activeDevice.bound_at ? formatDate(activeDevice.bound_at) : '—'}
                        {activeDevice.last_seen_at ? ` · Último uso: ${formatDate(activeDevice.last_seen_at)}` : ''}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Sin dispositivo vinculado. Se vinculará en el próximo self check-in.
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={isResettingDevice || isDeviceLoading || !activeDevice}
                  onClick={handleResetDevice}
                >
                  {isResettingDevice ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1.5 hidden sm:inline">Reasignar</span>
                </Button>
              </div>
            </div>

            {/* History button */}
            <div className="px-5 pb-5">
              <Button
                variant={showHistory ? 'secondary' : 'default'}
                className="w-full"
                onClick={handleLoadHistory}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Ver Historial de Pagos
              </Button>
            </div>
          </div>

          {/* Payment History */}
          {showHistory && (
            <div className="space-y-4">
              {isHistoryLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full rounded-xl" />
                  <Skeleton className="h-20 w-full rounded-lg" />
                  <Skeleton className="h-20 w-full rounded-lg" />
                </div>
              ) : (
                <>
                  {/* Summary Card */}
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-medium">Total Gastado</p>
                        <p className="text-2xl font-bold text-foreground">{formatCurrency(totalSpent)}</p>
                      </div>
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {transactions.length} transaccion{transactions.length !== 1 ? 'es' : ''}
                    </p>
                  </div>

                  {/* Transactions Timeline */}
                  {transactions.length > 0 ? (
                    <div className="space-y-3">
                      {transactions.map((tx) => (
                        <div
                          key={`${tx.type}-${tx.id}`}
                          className="p-4 rounded-lg bg-muted/50 border border-border transition-colors hover:bg-muted"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                              <div
                                className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                                  tx.type === 'membership'
                                    ? 'bg-success/20 text-success'
                                    : 'bg-warning/20 text-warning'
                                }`}
                              >
                                {tx.type === 'membership' ? (
                                  <CreditCard className="h-4 w-4" />
                                ) : (
                                  <ShoppingCart className="h-4 w-4" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold truncate">{tx.description}</p>
                                  <Badge
                                    variant={tx.type === 'membership' ? 'success' : 'warning'}
                                    className="text-[10px] shrink-0"
                                  >
                                    {tx.type === 'membership' ? 'Membresía' : 'Venta'}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {formatDate(tx.date)}
                                </p>
                                {tx.type === 'membership' && tx.membership_start && (
                                  <p className="text-[10px] text-muted-foreground mt-0.5">
                                    {formatLocalDate(tx.membership_start)} → {formatLocalDate(tx.membership_end || '')}
                                  </p>
                                )}
                                {tx.payment_method && (
                                  <p className="text-[10px] text-muted-foreground uppercase mt-0.5">
                                    {tx.payment_method}
                                  </p>
                                )}
                                {tx.items && tx.items.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {tx.items.map((item, i) => (
                                      <div key={i} className="flex justify-between text-xs text-muted-foreground">
                                        <span>{item.quantity}x {item.product_name}</span>
                                        <span className="font-mono">{formatCurrency(item.unit_price)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="text-sm font-bold text-foreground whitespace-nowrap">
                              {formatCurrency(tx.amount)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Receipt className="h-12 w-12 mx-auto mb-3 opacity-20" />
                      <p>Sin historial de pagos</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
